import React, { useEffect, useState } from "react";
import { FaBell, FaBellSlash } from "react-icons/fa";
import { theme, t } from "../theme";
import {
  isPushSupported,
  getPushStatus,
  enablePush,
  disablePush,
  sendTestPush,
} from "../pwa/pushClient";
import { getDisplayMode } from "../pwa/telemetry";
import * as pushNativo from "../platform/push";

const labelFor = (status, displayMode) => {
  if (status === "unsupported") {
    if (displayMode === "browser" && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
      return {
        title: "Adicione o Metria à Tela de Início",
        desc:
          'No Safari, toque em "Compartilhar" → "Adicionar à Tela de Início". As notificações do iOS só funcionam pelo app instalado.',
        tone: "info",
      };
    }
    return {
      title: "Navegador sem suporte a notificações",
      desc: "Tente abrir o Metria em outro navegador (Chrome, Edge, Safari).",
      tone: "warn",
    };
  }
  if (status === "denied") {
    return {
      title: "Permissão negada",
      desc:
        "Você bloqueou notificações para o Metria. Ative nas preferências do navegador/sistema e recarregue a página.",
      tone: "warn",
    };
  }
  if (status === "granted-subscribed") {
    return {
      title: "Notificações ativas",
      desc: "Você receberá alertas de estoque e lotes vencendo neste dispositivo.",
      tone: "ok",
    };
  }
  return {
    title: "Notificações desativadas",
    desc:
      "Ative para receber alertas de estoque baixo e lotes vencendo direto no seu dispositivo, mesmo com o app fechado.",
    tone: "default",
  };
};

const toneStyles = {
  ok: { bg: "#E8F5EE", color: "#1E7A3D", border: "#C5E5D2" },
  warn: { bg: "#FFF7E6", color: "#9A6300", border: "#F3DBA8" },
  info: { bg: "#EAF3FF", color: "#1A4A8A", border: "#C8DCF7" },
  default: { bg: theme.colors.bgSection, color: theme.colors.text, border: theme.colors.borderLight },
};

const NotificacoesCard = () => {
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const displayMode = getDisplayMode();

  // Este card e da WEB. No app o push e FCM e quem liga e desliga e o sistema
  // operacional — ver o early return la embaixo.
  const nativo = pushNativo.disponivel();

  const refresh = async () => {
    if (nativo) return;
    if (!isPushSupported()) { setStatus("unsupported"); return; }
    setStatus(await getPushStatus());
  };

  useEffect(() => { refresh(); }, []);

  const handleEnable = async () => {
    setBusy(true); setMsg(null);
    const r = await enablePush();
    setBusy(false);
    if (r.ok) {
      setMsg("Notificações ativadas. Enviamos uma de teste.");
      await sendTestPush().catch(() => {});
    } else if (r.reason === "permission-denied") {
      setMsg("Permissão negada — habilite manualmente nas preferências do navegador.");
    } else if (r.reason?.startsWith("subscribe-")) {
      setMsg("Não foi possível registrar este dispositivo. Em iOS, instale o app na Tela de Início primeiro.");
    } else if (r.reason === "server-disabled") {
      setMsg("Notificações ainda não configuradas no servidor. Avise o administrador.");
    } else {
      setMsg("Falha ao ativar. Tente novamente.");
    }
    refresh();
  };

  const handleDisable = async () => {
    setBusy(true); setMsg(null);
    const r = await disablePush();
    setBusy(false);
    if (r.ok) setMsg("Notificações desativadas neste dispositivo.");
    refresh();
  };

  // NO APP, ESTA SECAO NAO EXISTE. Quem decide se o Metria pode notificar no
  // celular e o sistema operacional, nos ajustes dele — a pagina conseguia
  // ligar, mas nunca desligar. Um botao "ativar" sem o "desativar" ao lado
  // fazia a confeiteira procurar aqui um desligamento que so existe fora.
  // A permissao ja e pedida no primeiro uso; na web o card continua inteiro,
  // porque la a inscricao de Web Push e mesmo nossa para criar e cancelar.
  if (nativo) return null;

  if (status === "loading") return null;

  const meta = labelFor(status, displayMode);
  const tone = toneStyles[meta.tone] || toneStyles.default;
  const isOn = status === 'granted-subscribed';

  return (
    <section style={{
      background: '#fff', padding: 20, borderRadius: 12,
      border: `1px solid ${theme.colors.borderLight}`, marginBottom: 24,
    }}>
      <h3 style={{
        marginTop: 0, marginBottom: 14, fontSize: 16, fontWeight: 700,
        display: 'flex', alignItems: 'center', gap: 8, color: theme.colors.text,
      }}>
        {isOn ? <FaBell color={theme.colors.primary} /> : <FaBellSlash color={theme.colors.textSecondary} />}
        Notificações por push
      </h3>

      <div style={{
        background: tone.bg, color: tone.color, border: `1px solid ${tone.border}`,
        borderRadius: 10, padding: '12px 14px', marginBottom: 12,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{meta.title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.4 }}>{meta.desc}</div>
      </div>

      {msg && (
        <div style={{
          fontSize: 13, color: theme.colors.textSecondary,
          padding: '8px 0', marginBottom: 8,
        }}>{msg}</div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(status === 'granted-not-subscribed' || status === 'default') && (
          <button onClick={handleEnable} disabled={busy} style={t.btnPrimary}>
            {busy ? 'Ativando...' : 'Ativar notificações'}
          </button>
        )}
        {isOn && (
          <button onClick={handleDisable} disabled={busy} style={{
            ...t.btnSecondary, color: theme.colors.danger,
            borderColor: theme.colors.danger + '40',
          }}>
            Desativar
          </button>
        )}
      </div>

      <div style={{
        marginTop: 12, fontSize: 11, color: theme.colors.textMuted,
        display: 'flex', gap: 12, flexWrap: 'wrap',
      }}>
        {/* Diagnostico para suporte. */}
        <span>Modo: <strong>{displayMode}</strong></span>
        <span>Status: <strong>{status}</strong></span>
      </div>
    </section>
  );
};

export default NotificacoesCard;
